import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// AuthProvider pulls in supabase-js (~75 KB gzip). It is NOT needed for first
// paint — the hero is fully renderable without auth state. Defer mounting until
// the browser is idle so the supabase chunk doesn't compete with hero LCP for
// bandwidth on slow mobile connections.
//
// QueryClientProvider, on the other hand, MUST be available eagerly: any route
// (e.g. SalesPage) that calls `useQuery` on first render will throw
// "No QueryClient set" without it. react-query is small enough to bundle
// eagerly without hurting LCP.
const DeferredAuthOnly = lazy(() => import("@/components/DeferredAuthOnly"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

interface DeferredAuthProvidersProps {
  children: ReactNode;
}

const DeferredAuthProviders = ({ children }: DeferredAuthProvidersProps) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: number | undefined;

    // NOTE: We intentionally do NOT trigger `setReady` on user interaction
    // (pointerdown/touchstart/keydown). Flipping `ready` swaps this
    // component's returned element type at the `children` slot
    // (children → <Suspense><Lazy>{children}</Lazy></Suspense>), which
    // unmounts and remounts the entire routed subtree. Doing that in
    // response to the user's first tap destroys the DOM node the click
    // fired on, so its state update (open menu, open dialog, focus input)
    // lands on a doomed component and appears as a swallowed tap.
    //
    // requestIdleCallback only fires during genuinely idle frames, so it
    // won't preempt an active gesture. Auth-dependent UI (sign-in dialog,
    // profile menu) still becomes fully live within ~2.5s at the latest.
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(() => setReady(true), { timeout: 2500 });
    } else {
      timeoutId = window.setTimeout(() => setReady(true), 1500);
    }

    return () => {
      if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {ready ? (
        <Suspense fallback={children}>
          <DeferredAuthOnly>{children}</DeferredAuthOnly>
        </Suspense>
      ) : (
        // Render children without AuthProvider — useAuth() returns a safe default
        // state (no user, no-op signIn/signUp), so Header and route pages render
        // their unauthenticated variant for this brief window.
        children
      )}
    </QueryClientProvider>
  );
};

export default DeferredAuthProviders;
